import ApplicationHeader from "./application_header";
import type Application from "@/types/application";
import { Stack, Row } from "@sprout/layout";
import { Tag } from "@sprout/tag";
import Divider from "@sprout/divider";
import { Body, SecondaryBody } from "@sprout/typography";
import DreamJobIcon from "@/sprout/icons/dream_job";

import "./selected_application.scss";
import { useTranslation } from "react-i18next";
import { daysUntilNextMonth } from "@/utils/date_utils";
import { useEffect, useState } from "react";
import DreamJobModal from "./dream_job_modal";
import "@/stylesheets/dream_job_icon.scss";
import DreamJobInfoModal from "./dream_job_info_modal";
import { IconButton } from "@/sprout/icon_button";
import CloseIcon from "@/sprout/icons/close";
import { useLocalStorage } from "react-use";
import Tooltip from "@/sprout/tooltip";
import ErrorIcon from "@/sprout/icons/error";
import { SecondaryButton } from "@sprout/buttons";
import Stages from "@components/applications/stages.tsx";

type Props = {
  application: Application;
  hasTokens: boolean;
  onViewParentApplication: (id: number) => void;
};

const SelectedApplication = ({ application, hasTokens, onViewParentApplication }: Props) => {
  const { t } = useTranslation("applications", { keyPrefix: "dream_job_callout" });
  const [isDreamJobModalOpen, setIsDreamJobModalOpen] = useState(false);

  // We need to keep track of the current application in state so that we can update the dream_job tag
  const [currentApplication, setCurrentApplication] = useState(application);
  const [showCallout, setShowCallout] = useLocalStorage("SHOW_DREAM_JOB_CALLOUT", hasTokens);

  useEffect(() => {
    setCurrentApplication(application);
  }, [application]);

  const onAddDreamJob = async () => {
    setCurrentApplication({ ...currentApplication, dream_job: true });
  };

  const handleViewFirstApplication = () => {
    if (currentApplication.duplicate_of_application_id) {
      onViewParentApplication(currentApplication.duplicate_of_application_id);
    }
  };

  return (
    <Stack gap="16px">
      <ApplicationHeader
        application={currentApplication}
        hasTokens={hasTokens}
        onAddDreamJob={onAddDreamJob}
      />
      <Stack gap="24px">
        <Row gap="8px" align="center">
          {currentApplication.locations && (
            <Tooltip text={currentApplication.locations}>
              <Tag label={currentApplication.locations} maxWidth="max-w-52" />
            </Tooltip>
          )}
          {/* Salary is out of scope */}
          <SecondaryBody>{currentApplication.status_message}</SecondaryBody>
        </Row>
        {showCallout &&
          !currentApplication.dream_job &&
          !currentApplication.inactive &&
          !currentApplication.duplicate &&
          hasTokens && (
            <Row gap="8px" align="center" className="dream-job-callout">
              <div className="w-full bg-marigold-25 px-6 py-4 border-marigold-e50 rounded-sm">
                <div className="flex flex-col gap-4 items-start">
                  <Stack gap="0">
                    <Stack gap="8px">
                      <Row align="center" gap="4px">
                        <div className="bg-marigold-e50 self-center dream-job-icon">
                          <DreamJobIcon size="sm" />
                        </div>
                        <p className="text-xs self-center dream-job-callout__time-left">
                          {t("days_left", { count: daysUntilNextMonth() })}
                        </p>
                        <IconButton
                          icon={CloseIcon}
                          size="md"
                          label={t("close")}
                          onClick={() => setShowCallout(false)}
                        />
                      </Row>
                      <Body medium>{t("title")}</Body>
                    </Stack>
                    <Row>
                      <p className="text-sm w-full">
                        {t("description")}
                        <span className="ml-2">
                          <DreamJobInfoModal secondaryLink />
                        </span>
                      </p>
                    </Row>
                  </Stack>
                  <SecondaryButton size="medium" onClick={() => setIsDreamJobModalOpen(true)}>
                    {t("button")}
                  </SecondaryButton>
                  <DreamJobModal
                    application={currentApplication}
                    afterSuccess={onAddDreamJob}
                    open={isDreamJobModalOpen}
                    onOpenChange={setIsDreamJobModalOpen}
                  />
                </div>
              </div>
            </Row>
          )}
        {currentApplication.inactive && currentApplication.duplicate && (
          <Row gap="8px" align="center" className="duplicate-callout">
            <div className="w-full bg-marigold-25 px-6 py-4 border-marigold-e50 rounded-sm">
              <div className="flex flex-col gap-4 xl:flex-row items-start">
                <Stack gap="0">
                  <Row gap="4px" align="center">
                    <ErrorIcon size="md" color="light-yellow" />
                    <p className="text-sm w-full">{t("duplicate_application.description")}</p>
                  </Row>
                </Stack>

                {currentApplication.duplicate_of_application_id && (
                  <SecondaryButton size="medium" onClick={handleViewFirstApplication}>
                    {t("duplicate_application.button")}
                  </SecondaryButton>
                )}
              </div>
            </div>
          </Row>
        )}
        <Stack gap="24px">
          <Divider />
          <div className="application--content">
            <div
              className="job-post-contents"
              dangerouslySetInnerHTML={{ __html: currentApplication.description }}
            />
            <Stages application={currentApplication} />
          </div>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SelectedApplication;
